import type React from "react"
import { Document, Page, Text, View, StyleSheet, Image, Link } from "@react-pdf/renderer"
import type { ZoneKey } from "../../types/locations"

export interface PropertyPDFProps {
  properties: any[]
  selectedZones: ZoneKey[]
  selectedProvinces: string[]
  selectedCantons: string[]
  selectedDistricts: string[]
  currencyFilter: string
  filterType: string
  generatedBy: string
  userPhotoUrl?: string
  userEmail?: string
}

// Función para determinar la zona de una propiedad
const getPropertyZone = (property: any, zoneMapping: Record<ZoneKey, string[]>): ZoneKey => {
  const locationKey = `${property.province || ""}-${property.county || ""}`

  for (const [zone, locations] of Object.entries(zoneMapping)) {
    if (locations.includes(locationKey)) {
      return zone as ZoneKey
    }
  }

  // Verificar si está en el GAM (simplificado)
  const gamProvinces = ["San José", "Alajuela", "Heredia", "Cartago"]
  if (gamProvinces.includes(property.province || "")) {
    return "GAM"
  }

  return "Zona Desconocida"
}

// Función para truncar texto
const truncateText = (text: string | undefined, maxLength: number): string => {
  if (!text) return "N/A"
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
}

// Estilos para el PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
  },
  section: {
    margin: 5,
    padding: 5,
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: "center",
    color: "#2c3e50",
    fontWeight: "bold",
  },
  text: {
    fontSize: 10,
    marginBottom: 5,
    color: "#2c3e50",
  },
  bold: {
    fontWeight: "bold",
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bdc3c7",
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bdc3c7",
    minHeight: 22,
    alignItems: "center",
  },
  tableHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bdc3c7",
    backgroundColor: "#3498db",
    minHeight: 22,
    alignItems: "center",
  },
  tableColID: {
    width: "6%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColTitle: {
    width: "20%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColCategory: {
    width: "12%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColPrice: {
    width: "12%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColCurrency: {
    width: "6%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColMode: {
    width: "10%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColCommission: {
    width: "10%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColServices: {
    width: "14%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableColZone: {
    width: "10%",
    borderStyle: "solid",
    borderRightWidth: 1,
    borderRightColor: "#bdc3c7",
    paddingLeft: 3,
    paddingRight: 3,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tableHeaderCell: {
    color: "#ffffff",
    fontWeight: "bold",
    fontSize: 8,
  },
  tableCell: {
    fontSize: 7,
    color: "#2c3e50",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    fontSize: 8,
    textAlign: "center",
    color: "#7f8c8d",
  },
  companyLogo: {
    width: 100,
    height: 50,
    objectFit: "contain",
    alignSelf: "center",
    marginBottom: 5,
  },
  date: {
    fontSize: 8,
    marginBottom: 5,
    textAlign: "right",
    color: "#7f8c8d",
  },
  filterSection: {
    marginBottom: 5,
  },
  filterTitle: {
    fontSize: 10,
    marginBottom: 3,
    color: "#34495e",
    fontWeight: "bold",
  },
  filterContent: {
    fontSize: 8,
    color: "#7f8c8d",
  },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8,
    textAlign: "center",
    color: "#7f8c8d",
  },
  alternateRow: {
    backgroundColor: "#f9f9f9",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 10,
  },
  profileSection: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 5,
    marginBottom: 5,
    padding: 3,
    backgroundColor: "#f8f9fa",
    borderRadius: 3,
  },
  profilePhoto: {
    width: 25,
    height: 25,
    borderRadius: 12.5,
    marginRight: 8,
  },
  profileText: {
    fontSize: 9,
    color: "#2c3e50",
  },
  linkText: {
    fontSize: 8,
    color: "#3498db",
    textDecoration: "underline",
    textAlign: "center",
    marginTop: 5,
  },
  infoSection: {
    marginBottom: 8,
    padding: 5,
    backgroundColor: "#f8f9fa",
    borderRadius: 3,
  },
  bulletPoint: {
    fontSize: 8,
    color: "#7f8c8d",
    marginLeft: 10,
    marginBottom: 2,
  },
})

// Definición del mapeo de zonas
const zoneMapping: Record<ZoneKey, string[]> = {
  "Noroeste (GAM)": ["San José-Santa Ana", "Alajuela-Alajuela", "Heredia-Belén"],
  "Noreste (GAM)": ["San José-Montes de Oca", "San José-Goicoechea", "Cartago-La Unión", "Heredia-Heredia"],
  "Suroeste (GAM)": ["San José-Escazú", "San José-Mora"],
  "Sureste (GAM)": ["San José-Desamparados", "Cartago-Cartago"],
  "Norte (Fuera del GAM)": ["Alajuela-San Carlos", "Heredia-Sarapiquí", "Alajuela-Upala"],
  "Sur (Fuera del GAM)": ["San José-Pérez Zeledón", "Puntarenas-Buenos Aires", "Puntarenas-Osa"],
  "Oeste (Fuera del GAM)": ["Puntarenas-Puntarenas", "Puntarenas-Esparza"],
  "Este (Fuera del GAM)": ["Cartago-Turrialba", "Limón-Limón"],
  GAM: [],
  "Zona Desconocida": [],
}

// Función para extraer servicios clave de un objeto JSON o texto
const extractKeyServices = (servicesData: string | undefined): string => {
  if (!servicesData) return "N/A"

  try {
    // Intentar parsear como JSON
    const servicesObj = JSON.parse(servicesData)

    // Lista de servicios clave a buscar
    const keyServices = ["agua", "electricidad", "internet", "seguridad", "agua potable", "electricity", "security"]

    // Extraer servicios clave
    const foundServices: string[] = []
    Object.entries(servicesObj).forEach(([key, value]) => {
      if (keyServices.some((service) => key.toLowerCase().includes(service.toLowerCase()))) {
        foundServices.push(`${key}`)
      }
    })

    return foundServices.length > 0 ? foundServices.join(", ") : "Básicos"
  } catch (e) {
    // Si no es JSON, devolver una versión truncada del texto
    return servicesData.length > 20 ? servicesData.substring(0, 20) + "..." : servicesData
  }
}

// Componente para el PDF
const PropertyPDF: React.FC<PropertyPDFProps> = ({
  properties,
  selectedZones,
  selectedProvinces,
  selectedCantons,
  selectedDistricts,
  currencyFilter,
  filterType,
  generatedBy,
  userPhotoUrl,
  userEmail,
}) => {
  // Calcular propiedades por página (aproximadamente 15-20 propiedades por página)
  const propertiesPerPage = 18;
  const totalPages = Math.ceil(properties.length / propertiesPerPage);

  return (
    <Document>
      {/* Primera página - Información general */}
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Encabezado con logo de la empresa */}
        <View style={styles.headerContainer}>
          <Image src="/assets/images/logo-alien1-v3.png" style={styles.companyLogo} />
          <Text style={styles.header}>Reporte de Propiedades</Text>
          
          {/* Sección de perfil del usuario */}
          <View style={styles.profileSection}>
            <Image src={userPhotoUrl || "/placeholder.svg"} style={styles.profilePhoto} />
            <Text style={styles.profileText}>
              Este reporte fue generado por: <Text style={{ fontWeight: "bold" }}>{generatedBy || "Usuario"}</Text>
            </Text>
          </View>
        </View>

        <Text style={styles.date}>Fecha: {new Date().toLocaleDateString("es-CR")}</Text>

        {/* Sección de filtros aplicados */}
        <View style={styles.filterSection}>
          <Text style={styles.filterTitle}>Filtros aplicados:</Text>

          {selectedZones.length > 0 && <Text style={styles.filterContent}>Zonas: {selectedZones.join(", ")}</Text>}

          {selectedProvinces.length > 0 && (
            <Text style={styles.filterContent}>Provincias: {selectedProvinces.join(", ")}</Text>
          )}

          {selectedCantons.length > 0 && (
            <Text style={styles.filterContent}>
              Cantones: {selectedCantons.map((canton) => canton.split("-")[1]).join(", ")}
            </Text>
          )}

          {selectedDistricts.length > 0 && (
            <Text style={styles.filterContent}>
              Distritos: {selectedDistricts.map((district) => district.split("-")[2]).join(", ")}
            </Text>
          )}

          {currencyFilter !== "all" && <Text style={styles.filterContent}>Moneda: {currencyFilter}</Text>}

          {filterType !== "none" && (
            <Text style={styles.filterContent}>
              Ordenado por:{" "}
              {filterType === "date"
                ? "Fecha"
                : filterType === "sale"
                  ? "Solo Venta"
                  : filterType === "rent"
                    ? "Solo Alquiler"
                    : filterType === "price_high"
                      ? "Precio: Mayor a menor"
                      : filterType === "price_low"
                        ? "Precio: Menor a mayor"
                        : ""}
            </Text>
          )}
        </View>

        <Text style={styles.text}>
          <Text style={styles.bold}>Total de propiedades: </Text>
          {properties.length}
        </Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Páginas de datos: </Text>
          {totalPages}
        </Text>

        {/* Resumen por zonas */}
        <View style={styles.section}>
          <Text style={styles.filterTitle}>Resumen por zonas:</Text>
          {(() => {
            const zoneSummary = properties.reduce((acc, property) => {
              const zone = getPropertyZone(property, zoneMapping);
              acc[zone] = (acc[zone] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);

            return Object.keys(zoneSummary).map((zone) => (
              <Text key={zone} style={styles.filterContent}>
                {zone}: {zoneSummary[zone]} propiedades
              </Text>
            ));
          })()}
        </View>

        {/* Información sobre cómo usar el reporte */}
        <View style={styles.section}>
          <Text style={styles.filterTitle}>Cómo usar este reporte:</Text>
          <Text style={styles.filterContent}>
            • Cada propiedad tiene un #ID único para búsqueda rápida en nuestro muro digital
          </Text>
          <Text style={styles.filterContent}>
            • Los precios se muestran en moneda original (USD, CRC)
          </Text>
          <Text style={styles.filterContent}>
            • Comisión estándar 50-50 salvo indicación contraria
          </Text>
        </View>

        {/* Muro Digital - Sección destacada */}
        <View style={[styles.section, { backgroundColor: "#e8f4fd", padding: 8, borderRadius: 5 }]}>
          <Text style={[styles.filterTitle, { textAlign: "center", marginBottom: 5 }]}>
            🌐 Muro Digital de Propiedades
          </Text>
          <Text style={[styles.filterContent, { textAlign: "center", marginBottom: 5 }]}>
            Busque propiedades por #ID, vea fotos completas y contacte directamente
          </Text>
          <Link src="https://muro.alienrealty.orbitascr.com" style={[styles.linkText, { fontSize: 10, fontWeight: "bold" }]}>
            <Text>muro.alienrealty.orbitascr.com</Text>
          </Link>
        </View>

        {/* Información de contacto personalizada */}
        <View style={styles.section}>
          <Text style={styles.filterTitle}>Contacto del Agente:</Text>
          <Text style={styles.filterContent}>
            📧 {userEmail || "info@alienrealty.orbitascr.com"}
          </Text>
        </View>

        {/* Nota legal simplificada */}
        <View style={styles.section}>
          <Text style={[styles.filterContent, { fontSize: 7, fontStyle: "italic", textAlign: "center" }]}>
            * Precios sujetos a cambios. Para información actualizada consulte nuestro muro digital.
          </Text>
        </View>

        {/* Pie de página */}
        <Text style={styles.footer}>
          Alien Realty © {new Date().getFullYear()} | {new Date().toLocaleDateString("es-CR")} a las{" "}
          {new Date().toLocaleTimeString("es-CR")}
        </Text>

        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
      </Page>

      {/* Páginas de tabla de propiedades */}
      {Array.from({ length: totalPages }, (_, pageIndex) => {
        const startIndex = pageIndex * propertiesPerPage;
        const endIndex = Math.min(startIndex + propertiesPerPage, properties.length);
        const pageProperties = properties.slice(startIndex, endIndex);

        return (
          <Page key={`table-page-${pageIndex}`} size="A4" orientation="landscape" style={styles.page}>
            {/* Encabezado simplificado para páginas de tabla */}
            <View style={styles.headerContainer}>
              <Text style={[styles.header, { fontSize: 14, marginBottom: 5 }]}>
                Propiedades - Página {pageIndex + 1} de {totalPages}
              </Text>
            </View>

            {/* Tabla de propiedades */}
            <View style={styles.table}>
              {/* Encabezado de la tabla */}
              <View style={styles.tableHeaderRow}>
                <View style={styles.tableColID}>
                  <Text style={styles.tableHeaderCell}>ID</Text>
                </View>
                <View style={styles.tableColTitle}>
                  <Text style={styles.tableHeaderCell}>Título</Text>
                </View>
                <View style={styles.tableColCategory}>
                  <Text style={styles.tableHeaderCell}>Categoría</Text>
                </View>
                <View style={styles.tableColServices}>
                  <Text style={styles.tableHeaderCell}>Servicios</Text>
                </View>
                <View style={styles.tableColPrice}>
                  <Text style={styles.tableHeaderCell}>Precio</Text>
                </View>
                <View style={styles.tableColCurrency}>
                  <Text style={styles.tableHeaderCell}>Moneda</Text>
                </View>
                <View style={styles.tableColMode}>
                  <Text style={styles.tableHeaderCell}>Modalidad</Text>
                </View>
                <View style={styles.tableColCommission}>
                  <Text style={styles.tableHeaderCell}>Comisión</Text>
                </View>
                <View style={styles.tableColZone}>
                  <Text style={styles.tableHeaderCell}>Zona</Text>
                </View>
              </View>

              {/* Filas de datos */}
              {pageProperties.map((property, index) => {
                const propertyZone = getPropertyZone(property, zoneMapping);
                const keyServices = extractKeyServices(property.services);
                const globalIndex = startIndex + index;

                return (
                  <View key={property.id_Property} style={[styles.tableRow, globalIndex % 2 === 1 ? styles.alternateRow : {}]}>
                    <View style={styles.tableColID}>
                      <Text style={styles.tableCell}>#{property.id_Property}</Text>
                    </View>
                    <View style={styles.tableColTitle}>
                      <Text style={styles.tableCell}>{truncateText(property.title, 35) || "N/A"}</Text>
                    </View>
                    <View style={styles.tableColCategory}>
                      <Text style={styles.tableCell}>{property.category || property.categoryName || "N/A"}</Text>
                    </View>
                    <View style={styles.tableColServices}>
                      <Text style={styles.tableCell}>{keyServices}</Text>
                    </View>
                    <View style={styles.tableColPrice}>
                      <Text style={styles.tableCell}>{property.price?.toLocaleString() || "N/A"}</Text>
                    </View>
                    <View style={styles.tableColCurrency}>
                      <Text style={styles.tableCell}>{property.currency || "N/A"}</Text>
                    </View>
                    <View style={styles.tableColMode}>
                      <Text style={styles.tableCell}>{property.modeDescription || "N/A"}</Text>
                    </View>
                    <View style={styles.tableColCommission}>
                      <Text style={styles.tableCell}>{property.commission || "50-50"}</Text>
                    </View>
                    <View style={styles.tableColZone}>
                      <Text style={styles.tableCell}>{propertyZone}</Text>
                    </View>
                  </View>
                );
              })}
            </View>

            {/* Pie de página para páginas de tabla */}
            <Text style={styles.footer}>
              Alien Realty © {new Date().getFullYear()} | Propiedades {startIndex + 1} - {endIndex} de {properties.length}
            </Text>

            <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} fixed />
          </Page>
        );
      })}
    </Document>
  );
};

export default PropertyPDF